import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/atoms/Button'
import { CheckBox } from '@/atoms/CheckBox/CheckBox'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { TextArea } from '@/atoms/Input/TextArea'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { PillGroup } from '@/atoms/PillGroup/PillGroup'
import { StarRatingInput } from '@/atoms/StarRatingInput'
import { ParagraphSM, TitleLG, TitleSM } from '@/atoms/Text'
import { useSafeTrack } from '@/utils/analytics'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

const textAreaInputClasses =
  'w-full rounded-[10px] border-[1.5px] border-core-gray-300 p-4 focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent'

export const ParentalControlsFeedbackView: React.FC = () => {
  const { t } = useTranslate('feedback')
  const track = useSafeTrack()
  const { query } = useSafeRouter()

  const workedAsExpectedOptions = useMemo(
    () => [
      { label: t('optionYes', 'Yes'), value: 'yes' },
      { label: t('optionNo', 'No'), value: 'no' },
    ],
    [t],
  )

  const contentId = getItemOrFirstEntry(query.content_id)
  const userId = getItemOrFirstEntry(query.user_id)
  const client = getItemOrFirstEntry(query.client)

  const [experienceRating, setExperienceRating] = useState<number | null>(null)
  const [workedAsExpected, setWorkedAsExpected] = useState<string | undefined>(undefined)
  const [explanation, setExplanation] = useState('')
  const [otherFeedback, setOtherFeedback] = useState('')
  const [canContact, setCanContact] = useState(true)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    track('Parental Controls Feedback Viewed', { contentId, userId, client })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = useCallback(() => {
    track('Parental Controls Feedback Submitted', {
      contentId,
      userId,
      client,
      experienceRating,
      workedAsExpected: workedAsExpected ?? null,
      explanation: workedAsExpected === 'no' ? explanation : null,
      otherFeedback: otherFeedback || null,
      canContact,
    })
    setSubmitted(true)
  }, [track, contentId, userId, client, experienceRating, workedAsExpected, explanation, otherFeedback, canContact])

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto flex min-h-screen max-w-[720px] flex-col items-center justify-center px-4">
          <AngelLogo height={24} color="black" />
          <CircleCheckIcon size={54} className="mt-5" />
          <TitleLG weight="bold" className="mt-5 text-center">
            {t('thankYouTitle', 'Thanks for Your Feedback!')}
          </TitleLG>
          <ParagraphSM color="core-gray-800" className="mt-3 text-center">
            {t('thankYouMessage', 'Your feedback has been submitted. Thanks for using Angel and helping us improve.')}
          </ParagraphSM>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-[720px] px-4 py-8">
        <div className="flex flex-col items-center">
          <AngelLogo color="black" height={24} className="lg:hidden" />
          <AngelLogo color="black" height={40} className="hidden lg:block" />
          <TitleLG weight="bold" className="mt-6 text-center">
            {t('parentalControlsTitle', 'Parental Controls Feedback')}
          </TitleLG>
          <ParagraphSM color="core-gray-800" className="mt-2 text-center">
            {t(
              'parentalControlsSubtitle',
              "Have feedback? We'd love to hear from you! Please fill out the form below to provide your feedback.",
            )}
          </ParagraphSM>
        </div>

        <div className="mt-10 space-y-8">
          <div>
            <TitleSM weight="semibold">
              {t('experienceQuestion', "How has your experience been with 'Parental Controls'?")}
            </TitleSM>
            <div className="mt-3">
              <StarRatingInput value={experienceRating} onChange={setExperienceRating} />
            </div>
          </div>

          <div>
            <TitleSM weight="semibold">
              {t('workedAsExpectedQuestion', 'Did parental controls work like you expected?')}
            </TitleSM>
            <div className="mt-3">
              <PillGroup
                options={workedAsExpectedOptions}
                value={workedAsExpected}
                onChange={setWorkedAsExpected}
                className="gap-1"
              />
            </div>

            {workedAsExpected === 'no' && (
              <div className="mt-4 pl-4">
                <TitleSM weight="semibold">{t('pleaseExplain', 'Please explain:')}</TitleSM>
                <TextArea
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  inputClasses={textAreaInputClasses}
                  containerClassName="!mt-2"
                  rows={3}
                />
              </div>
            )}
          </div>

          <div>
            <TitleSM weight="semibold">{t('otherFeedbackQuestion', 'What other feedback do you have?')}</TitleSM>
            <TextArea
              value={otherFeedback}
              onChange={(e) => setOtherFeedback(e.target.value)}
              placeholder={t('feedbackPlaceholder', 'Feedback...')}
              inputClasses={textAreaInputClasses}
              containerClassName="!mt-3"
              rows={5}
            />
          </div>

          <Button variant="oxide" size="md" className="w-full" onClick={handleSubmit}>
            {t('submitButton', 'Share My Feedback')}
          </Button>

          <CheckBox
            checked={canContact}
            onChange={(e) => setCanContact(e.target.checked)}
            size="sm"
            color="white"
            labelClassName="ml-3 cursor-pointer text-lg"
          >
            {t('contactConsent', 'The Angel team may contact me with follow-up questions.')}
          </CheckBox>
        </div>
      </div>
    </div>
  )
}
